/**
 * 统一的日期工具函数（纯函数，全局共享）
 *
 * 背景：JavaScript 中 `new Date("YYYY-MM-DD")` 按 UTC 解析为当天 0 点 UTC；
 * 之后调用 `.toISOString().slice(0, 10)` 仍是 UTC 日期。在东八区下
 * 本地日期 2026-05-02 会被误写成 "2026-05-01"。
 *
 * 本模块的所有日期字符串均为本地时区下的 YYYY-MM-DD，前后端契约一致。
 * 浏览器所在即业务时区（东八区），因此本地日历日就是后端的业务日；
 * 后端口径的唯一事实源见 `backend/src/utils/business-date.ts`。
 */

/** 将 Date 对象格式化为本地时区的 YYYY-MM-DD（不含时分秒） */
export function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 获取今天的本地日期字符串 YYYY-MM-DD */
export function todayString(): string {
  return formatLocalDate(new Date());
}

/** 基于今天向后偏移 n 天，返回 YYYY-MM-DD */
export function daysFromTodayString(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return formatLocalDate(d);
}

/**
 * 解析 YYYY-MM-DD / ISO 字符串为本地日期对象（时分秒为 00:00:00）。
 * - 对于裸 "YYYY-MM-DD"，显式拼 "T00:00:00" 按本地时区解析，避免 UTC 偏移。
 * - 对于完整 ISO 串，直接交给 Date 解析后再取本地 Y/M/D。
 */
export function parseLocalDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  // 纯日期
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(s + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * 把任意形式（Date / ISO / YYYY-MM-DD）的日期规范化为本地 YYYY-MM-DD。
 * 用于显示层和字段比较；空值返回 null。
 */
export function toLocalDateString(d: Date | string | null | undefined): string | null {
  if (!d) return null;
  if (typeof d === 'string') {
    // 已是 YYYY-MM-DD 则原样返回
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    return formatLocalDate(dt);
  }
  return formatLocalDate(d);
}

/**
 * 距今天多少个**日历日**：未来为正、过去为负、今天为 0；缺失或非法返回 null。
 *
 * 两端都先经 `parseLocalDate` 归到本地 0 点再取日差，不直接 `new Date("YYYY-MM-DD")` 做差：
 * 裸日期串按 UTC 零点解析，本地串按本地时区解析，混在一起就会凭空多算或少算一天。
 */
export function calendarDaysUntil(dateStr: string | null | undefined): number | null {
  const target = parseLocalDate(dateStr);
  const today = parseLocalDate(todayString());
  if (!target || !today) return null;
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

/**
 * 预订天数 —— 全系统唯一实现，「算钱的」和「显示的」都走这里。
 *
 * 契约（docs/接口契约.md「订单一律按整天计算」）：
 * - 只填预约日期，或结束日期与开始日同一天 → **1 天**。
 * - 填了结束日期 → 开始日当天到结束日当天**含两端**，即 `日期差 + 1`（9/5 ~ 9/11 = 7 天）。
 * - 开始日缺失或非法 → **null**，不是 1。预约日期是订单成立的前提，没有档期就没有天数，
 *   拿 1 冒充「不知道」会让明细合计静默算成单日金额——那是一张看起来完全正常的错价单。
 *   显示路径判 null 出「-」；金额路径判 null 时**不乘天数**、按单日口径预览（乘 0 会凭空造出一张 0 元单），
 *   提交前本就被「请填写预约日期」挡下，未知状态不会落到库里。
 *
 * 与后端 `utils/business-date.ts` 的 `bookingDays` 同一条款。
 * 用 parseLocalDate 归一到本地 0 点后再取日差，规避 `new Date("YYYY-MM-DD")` 的 UTC 偏移；
 * 禁用 `Math.ceil` 按毫秒向上取整——那会把整天口径算出「还剩 2 天」这类假半天。
 */
export function bookingDays(
  start: string | null | undefined,
  end: string | null | undefined,
): number | null {
  const s = parseLocalDate(start);
  if (!s) return null;
  const e = parseLocalDate(end);
  if (!e) return 1;
  const days = Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
  return days > 1 ? days : 1;
}
